import type { types } from "@fraction/shared";
import { HandIcon } from "lucide-react";
import { useCallback, useState } from "react";
import { openWorkflowConfirmationWindow } from "src/utilities/openWorkflowConfirmation";

import { NotificationCardLayout } from "./NotificationCardLayout";
import { WorkflowConfirmationContent } from "./WorkflowConfirmationContent";

export function WorkflowConfirmationCard({
  notification,
  onDismiss,
  count,
}: {
  notification: types.WorkflowConfirmationRequestNotification;
  onDismiss: () => void;
  count?: number;
}) {
  const [open, setOpen] = useState(false);

  // The opener decides: its own window on the desktop, the modal below in the browser.
  const handleClick = useCallback(() => {
    if (openWorkflowConfirmationWindow(notification)) return;
    setOpen(true);
  }, [notification]);

  return (
    <>
      <NotificationCardLayout
        icon={HandIcon}
        iconBg="bg-violet-100"
        iconColor="text-violet-600"
        title={`Confirmation needed${notification.workflowName ? ` · ${notification.workflowName}` : ""}`}
        applicationEid={notification.applicationEid}
        applicantNames={notification.applicantNames}
        onClick={handleClick}
        count={count}
      >
        <p className="line-clamp-2 text-sm text-foreground">{notification.question}</p>
        {notification.proposedAction ? (
          <p className="line-clamp-1 text-xs text-muted-foreground">
            Proposed: {notification.proposedAction}
          </p>
        ) : null}
      </NotificationCardLayout>

      {open ? (
        <WorkflowConfirmationModal
          notification={notification}
          onClose={() => setOpen(false)}
          onResolved={() => {
            setOpen(false);
            onDismiss();
          }}
        />
      ) : null}
    </>
  );
}

/** The browser's presentation: the same content, under a backdrop. */
function WorkflowConfirmationModal({
  notification,
  onClose,
  onResolved,
}: {
  notification: types.WorkflowConfirmationRequestNotification;
  onClose: () => void;
  onResolved: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div onClick={(event) => event.stopPropagation()}>
        <WorkflowConfirmationContent
          notification={notification}
          onClose={onClose}
          onResolved={onResolved}
          className="max-w-xl shadow-2xl"
        />
      </div>
    </div>
  );
}
